import { createAsyncThunk } from "@reduxjs/toolkit"
import { AxiosError } from "axios"

import type { RootState } from "../../store"

import {
  disconnectScannerApi,
  downloadScannerAttachment,
  downloadScannerFileFromUrlApi,
  fetchScannerAttachments,
  fetchScannerPatientListApi,
  getScannerConnectionStatusApi,
  getScannerRedirectUrlApi,
  loadScannerFilesApi,
  refreshScannerPatientsApi,
  triggerPatientListFetchApi,
} from "./integrations.service"
import type {
  downloadScannerAttachmentParams,
  fetchScannerAttachmentsParams,
  FetchScannerPatientListThunkArg,
  LoadScannerFilesParams,
  ScannerAttachmentsResult,
  ScannerConnectionRow,
  ScannerFilesLoadResponse,
} from "./integrations.type"

type ScannerConnectionStatusThunkResult = {
  org_id: string
  connections: ScannerConnectionRow[]
}

/** OAuth redirect URL; returns `{ redirect_url }` for callers (Settings, EasyRxConnect). */
export const getScannerRedirectUrl = createAsyncThunk(
  "integrationsService/getScannerRedirectUrl",
  async (
    arg: { scannerName?: string; scanner?: string } | undefined,
    { rejectWithValue, getState },
  ) => {
    const orgId = (getState() as RootState).userService.user.current_orgId
    const scanner = arg?.scannerName ?? arg?.scanner ?? "easyrx"
    try {
      const redirect_url = await getScannerRedirectUrlApi(orgId, scanner)
      return { redirect_url }
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

/**
 * GET /connection-status with `Scanner` header listing all integration ids (`ALL_SCANNER_INTEGRATION_IDS`, comma-separated). Redux maps `connections` via
 * `buildIntegrationConnectionStatus`; callers that need one row use `getScannerConnected`.
 */
export const getScannerConnectionStatus = createAsyncThunk(
  "integrationsService/getScannerConnectionStatus",
  async (_, { rejectWithValue, getState }) => {
    const orgId = (getState() as RootState).userService.user.current_orgId
    try {
      const result = await getScannerConnectionStatusApi(orgId)
      return {
        org_id: result.org_id,
        connections: result.connections,
      } satisfies ScannerConnectionStatusThunkResult
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const disconnectScanner = createAsyncThunk(
  "integrationsService/disconnectScanner",
  async (
    { scannerName }: { scannerName: string },
    { rejectWithValue, getState },
  ) => {
    const orgId = (getState() as RootState).userService.user.current_orgId
    try {
      await disconnectScannerApi(orgId, scannerName)
      return { scannerName }
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const triggerPatientListFetch = createAsyncThunk(
  "integrationsService/triggerPatientListFetch",
  async (
    { scannerName }: { scannerName: string },
    { rejectWithValue, getState },
  ) => {
    const orgId = (getState() as RootState).userService.user.current_orgId
    try {
      return await triggerPatientListFetchApi(orgId, scannerName)
    } catch (err) {
      const ax = err as AxiosError<{ message?: { message?: string } }>
      if (ax.response?.status === 412) {
        return rejectWithValue({
          message:
            ax.response.data?.message?.message ??
            `Scanner ${scannerName} not connected`,
        })
      }
      return rejectWithValue(err)
    }
  },
)

export const fetchScannerPatientList = createAsyncThunk(
  "integrationsService/fetchScannerPatientList",
  async (
    params: FetchScannerPatientListThunkArg,
    { rejectWithValue, getState },
  ) => {
    const orgId = (getState() as RootState).userService.user.current_orgId
    const { scannerName, isPolling: _isPolling, ...queryParams } = params
    try {
      const response = await fetchScannerPatientListApi(
        orgId,
        queryParams,
        scannerName,
      )
      return response
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

// Force Refresh / Delta Refresh
export const refreshScannerPatients = createAsyncThunk(
  "integrationsService/refreshScannerPatients",
  async (
    { scannerName }: { scannerName: string },
    { rejectWithValue, getState },
  ) => {
    const orgId = (getState() as RootState).userService.user.current_orgId
    try {
      return await refreshScannerPatientsApi(orgId, scannerName)
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

// Scanner attachment thunks (case-level, e.g. EasyRx) — legacy

/** @deprecated Use `useScannerFilesLoader` hook instead. */
export const fetchScannerAttachmentsList = createAsyncThunk<
  { data: { result: ScannerAttachmentsResult } },
  fetchScannerAttachmentsParams
>(
  "integrationsService/fetchScannerAttachments",
  async ({ caseId, scannerName }, { rejectWithValue, getState }) => {
    const orgId = (getState() as RootState).userService.user.current_orgId
    try {
      return await fetchScannerAttachments(orgId, caseId, scannerName)
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

/** @deprecated Use `downloadScannerFile` thunk instead. */
export const downloadScannerAttachmentFile = createAsyncThunk<
  File,
  downloadScannerAttachmentParams
>(
  "integrationsService/downloadScannerAttachment",
  async (
    { caseId, attachmentId, fileName, scannerName },
    { rejectWithValue, getState },
  ) => {
    const orgId = (getState() as RootState).userService.user.current_orgId
    try {
      return await downloadScannerAttachment(
        orgId,
        caseId,
        attachmentId,
        fileName,
        scannerName,
      )
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

// New scanner /files/load thunks

/** Single POST to /scanner/files/load. Used by `useScannerFilesLoader` on each poll iteration. */
export const loadScannerFiles = createAsyncThunk<
  ScannerFilesLoadResponse,
  LoadScannerFilesParams
>(
  "integrationsService/loadScannerFiles",
  async (
    { externalCaseId, scannerName, mode = "temporary" },
    { rejectWithValue, getState },
  ) => {
    const orgId = (getState() as RootState).userService.user.current_orgId
    try {
      return await loadScannerFilesApi(orgId, externalCaseId, scannerName, mode)
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

/** Fire-and-forget cleanup — calls /scanner/files/load with `mode: "confirmed"`. */
export const cleanupScannerFiles = createAsyncThunk<
  void,
  { externalCaseId: string | number; scannerName: string }
>(
  "integrationsService/cleanupScannerFiles",
  async ({ externalCaseId, scannerName }, { rejectWithValue, getState }) => {
    const orgId = (getState() as RootState).userService.user.current_orgId
    try {
      await loadScannerFilesApi(orgId, externalCaseId, scannerName, "confirmed")
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

/** Download a file from a pre-signed `download_url`. Tracks `activeDownloadsCount` for the spinner. */
export const downloadScannerFile = createAsyncThunk<
  File,
  { url: string; fileName: string; scannerName: string }
>(
  "integrationsService/downloadScannerFile",
  async ({ url, fileName, scannerName }, { rejectWithValue, signal }) => {
    try {
      return await downloadScannerFileFromUrlApi(
        url,
        fileName,
        scannerName,
        signal,
      )
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)
